'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import React, {
  createContext,
  ReactNode,
  RefObject,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback,
} from 'react';
import { AffiliateDetails, useAffiliateMetadata } from 'utils/affiliateHelpers';
import { Currency } from 'utils/Currency';
import { CurrencySymbol } from 'components/shared/CurrencySymbol';
import { formatPrice as formatPriceUtil } from 'hooks/pricing/useFormattedPriceNew';
import { useForwardSearchParamsShopify } from 'hooks/navigation/useForwardSearchParamsShopify';
import { getCampaignPromoDiscountConfig } from 'content/referral';
import { getCampaignConfig } from 'content/campaigns';
import {
  ShopifyStore,
  getEffectiveStoreForDiscount,
} from 'lib/commerce/shopify/shopifyHelper';
import { ShippingLabel } from 'components/uh-home/ShippingLabel';
import { useRegionLocale } from 'contexts/RegionLocaleContext';

// Products data
import {
  HomePrice,
  HomePriceStriked,
  productMap_home,
} from 'content/products/uhHome';

import { UhxShopifyMap } from 'content/products/uhx';
import { addToCartArgsType, ProductType } from 'components/shared/Cart/types';
import { tagBundle } from 'components/shared/Cart/bundle';
import { useCartStore, useShallow } from 'components/shared/Cart';

// Addons for X-Home (if any)
export type AddonsType = 'Year2' | 'Year1' | false | null;

// Type for shipping estimate data
export type ShippingEstimateObject = Record<
  string,
  {
    date: string;
    mcf_for_eta: boolean;
    show_mcf_logo: boolean;
    default_custom_eta: string;
  }
>;

// Main context type definition
export type HomeContextType = {
  // Basic properties
  defaultCurrency: Currency;
  currencySymbol?: React.ReactNode;
  store?: ShopifyStore;

  // Actions
  checkout: () => void;
  goToNextStep: () => void;
  isLoading: boolean;

  uhx: boolean;
  setUhx: (value: boolean) => void;

  uhxCart: AddonsType;
  setUhxCart: (value: AddonsType) => void;
  coverageRef?: RefObject<HTMLDivElement | null>;
  // Element references
  cartSummaryRef?: RefObject<HTMLDivElement | null>;
  addonRef?: RefObject<HTMLDivElement | null>;

  // Pricing details
  price: number;
  uhxPrice: number;
  effectivePrice: number;
  dueEffectivePrice: number;

  // Optional properties
  token?: string;
  shippingEstimate: ReactNode;
  affiliateDetails: AffiliateDetails | null;
  campaignActive: boolean;
  affiliateDiscount: number;
  birthdayDiscount: number;
  effectiveDiscount: { type: string; amount: number };
  referral?: {
    discountCode: string;
    discount: number;
  };

  // UI helpers
  formatPrice: (price: number) => string;
  displayPrice: (price: number) => React.ReactNode;
};

// Initial context state
const initialHomeContext: HomeContextType = {
  // Default values
  defaultCurrency: Currency.usd,
  checkout: () => undefined,
  goToNextStep: () => undefined,
  isLoading: false,

  uhx: false,
  setUhx: () => undefined,

  uhxCart: null,
  setUhxCart: () => undefined,

  // Price defaults
  price: 0,
  uhxPrice: 0,
  effectivePrice: 0,
  dueEffectivePrice: 0,

  // Other defaults
  shippingEstimate: null,
  affiliateDetails: null,
  campaignActive: false,
  affiliateDiscount: 0,
  birthdayDiscount: 0,
  effectiveDiscount: { type: 'referral', amount: 0 },
  referral: undefined,

  // Helper methods
  formatPrice: (price: number) => price.toString(),
  displayPrice: () => <></>,
};

// Create context
export const HomeContext = createContext<HomeContextType>(initialHomeContext);

// Custom hook for using the context
export const useHomeContext = () => useContext(HomeContext);

// Context provider props
interface HomeContextProviderProps {
  children?: ReactNode;
  currency: Currency;
  store?: ShopifyStore;
  directCheckout?: boolean;
  defaultAddon?: string | null;
}

// Context provider component
export const HomeContextProvider: React.FC<HomeContextProviderProps> = ({
  children,
  currency,
  store = ShopifyStore.ROW,
  directCheckout = false,
  // defaultAddon = null,
}) => {
  // Router for query parameters
  const router = useRouter();
  const searchParams = useSearchParams();
  const { region: country } = useRegionLocale();

  // Element references
  const cartSummaryRef = useRef<HTMLDivElement>(null);
  const addonRef = useRef<HTMLDivElement>(null);
  const coverageRef = useRef<HTMLDivElement>(null);

  // Get CartContext and campaign helper
  const CartClient = useCartStore(
    useShallow((s) => ({
      getCampaignForProduct: s.getCampaignForProduct,
      addToCart: s.addToCart,
    })),
  );
  const { getCampaignForProduct } = CartClient;

  // Get campaign data from CartContext
  const homeCampaign = getCampaignForProduct('home');
  const campaignConfig = getCampaignConfig(homeCampaign);

  // State management
  const campaignActive = campaignConfig?.active ?? false;
  const [isLoading, setIsLoading] = useState(false);
  // const [addon, setAddon] = useState<AddonsType>(null);

  // Affiliate data
  const affiliateCode = searchParams?.get('affiliateCode');
  const { affiliateDetails } = useAffiliateMetadata(affiliateCode as string);

  // Shopify helpers
  const forwardShopifySearchParams = useForwardSearchParamsShopify();
  // Get currency symbol
  const currencySymbol = CurrencySymbol[currency.toLowerCase() as Currency];
  // Format price helper method - uses the utility function, not the hook
  const formatPrice = useCallback(
    (price: number): string => {
      return formatPriceUtil(price, currency);
    },
    [currency],
  );

  // Display price with symbol - without using a hook inside
  const displayPrice = useCallback(
    (price: number): React.ReactNode => {
      const formatted = formatPriceUtil(price, currency);

      return (
        <>
          {currencySymbol}
          {formatted}
        </>
      );
    },
    [currency, currencySymbol],
  );

  // Element manipulation functions
  const flashTitle = useCallback(
    (elementRef: RefObject<HTMLDivElement | null>) => {
      if (!elementRef.current) return;

      const targetElement = elementRef.current.querySelector(
        '.section-row-title h5',
      ) as HTMLElement;

      if (targetElement) {
        targetElement.style.transition = 'transform 0.5s ease';
        targetElement.style.transform = 'scale(1.1)';

        // Use requestAnimationFrame for smoother animation
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            targetElement.style.transform = '';
          });
        });
      }
    },
    [],
  );

  // Check if element is visible
  const isVisible = useCallback(
    (elementRef: RefObject<HTMLDivElement | null>): boolean => {
      if (!elementRef.current) return false;

      const rect = elementRef.current.getBoundingClientRect();
      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <=
          (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <=
          (window.innerWidth || document.documentElement.clientWidth)
      );
    },
    [],
  );

  // Scroll to element
  const scrollToElement = useCallback(
    (elementRef: RefObject<HTMLDivElement | null>) => {
      elementRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    },
    [],
  );

  // Highlight element
  const highlightElement = useCallback(
    (elementRef: RefObject<HTMLDivElement | null>) => {
      if (!elementRef.current) return;

      isVisible(elementRef)
        ? flashTitle(elementRef)
        : scrollToElement(elementRef);
    },
    [isVisible, flashTitle, scrollToElement],
  );

  // Next step function
  const goToNextStep = useCallback(() => {
    if (!uhx) {
      highlightElement(coverageRef);
      return;
    }

    // All steps completed, proceed to checkout
    checkout();
  }, [addonRef, highlightElement]);

  const [shippingEstimate, setShippingEstimate] = useState<
    React.ReactNode | string | null
  >(null);

  const { store: cartStore } = useCartStore(
    useShallow((s) => ({ store: s.store })),
  );

  // Get shipping estimates on load
  useEffect(() => {
    setShippingEstimate(
      <ShippingLabel
        content={
          cartStore == ShopifyStore.IN
            ? 'Ships in 6-7 weeks'
            : 'Ships in 6-7 weeks'
        }
        justContent={true}
      />,
    );
  }, [cartStore]);

  // Base price
  const [price, setPrice] = useState(HomePrice[store]);

  // Get referral info
  // const referral = router.query.referral ? HomeReferralCodes[store] : undefined;
  const referral = undefined;

  // Special work affiliate handling
  const workAffiliate =
    searchParams?.get('workAffiliate') && searchParams?.get('discount');

  // Calculate affiliate discount
  const affiliateDiscount = useMemo(() => {
    // Exclude affiliate discount when affiliateCode=birthday
    if (searchParams?.get('affiliateCode') === 'birthday') {
      return 0;
    }

    // Work affiliate special discount
    if (workAffiliate) {
      if (
        searchParams?.get('affiliate') === 'on-running' ||
        searchParams?.get('invite') === 'on-running'
      )
        return price * 0.25; // 25% discount
      return price * 0.15; // 15% discount
    }

    // Handle regions with different affiliate structures
    const effectiveStore = getEffectiveStoreForDiscount(store);


    if (!effectiveStore) return 0;

    // Check conditions for applying affiliate discount
    if (
      !affiliateDetails ||
      // !affiliateDetails.homeActive ||
      !affiliateDetails.discount_configs[effectiveStore]?.HOME?.code
      // Removed router.query.referral condition to allow affiliate + birthday discounts
    )
      return 0;

    // Get discount configuration
    const discountConfig =
      affiliateDetails.discount_configs[effectiveStore]?.HOME;

    // Calculate discount based on type (percentage or fixed amount)
    return discountConfig?.type === 'percentage'
      ? (discountConfig.value * price) / 100
      : discountConfig?.value ?? 0;
  }, [
    campaignActive,
    affiliateDetails,
    price,
    workAffiliate,
    searchParams,
    store,
  ]);

  // --- Birthday Discount ---
  const birthdayDiscountCode = searchParams?.get('discount') as string;
  const birthdayDiscountPercentage = birthdayDiscountCode
    ? parseInt(birthdayDiscountCode.slice(-2)) || 15
    : 0; // Extract last 2 characters as percentage, default to 15%
  const birthdayDiscount =
    birthdayDiscountCode &&
    birthdayDiscountCode.length > 0 &&
    searchParams?.get('affiliateCode') === 'birthday'
      ? price * (birthdayDiscountPercentage / 100)
      : 0;

  // --- Home Promo Discount ---
  const homePromoDiscountConfig = useMemo(
    () =>
      getCampaignPromoDiscountConfig({
        store,
        country: searchParams?.get('country') as string,
        product: 'home',
        campaignConfig,
      }),
    [store, country, campaignConfig],
  );

  const homePromoDiscountAmount = useMemo(() => {
    if (
      !homePromoDiscountConfig?.active ||
      !homePromoDiscountConfig?.discount
    ) {
      return 0;
    }

    const discountValue = homePromoDiscountConfig.discount;
    const discountType = homePromoDiscountConfig.discountType;

    return discountType === 'percentage'
      ? price * (discountValue / 100)
      : discountValue;
  }, [homePromoDiscountConfig, price]);

  // Calculate all discounts and find the highest one
  const discounts = useMemo(
    () => [
      // { type: 'referral', amount: referral ? referral.discount : 0 },
      { type: 'affiliate', amount: affiliateDiscount },
      { type: 'birthday', amount: birthdayDiscount },
      { type: 'homePromoDiscount', amount: homePromoDiscountAmount },
    ],
    [referral, affiliateDiscount, birthdayDiscount, homePromoDiscountAmount],
  );

  const effectiveDiscount = useMemo(
    () =>
      discounts.reduce(
        (max, current) => (current.amount > max.amount ? current : max),
        { type: 'referral', amount: 0 },
      ),
    [discounts],
  );

  // Update price when affiliate discount or referral changes
  useEffect(() => {
    if (effectiveDiscount.amount > 0) {
      setPrice(HomePriceStriked[store]);
      return;
    }
    setPrice(HomePrice[store]);
  }, [effectiveDiscount.amount, store]);

  // Reset addon if affiliate selected
  // useEffect(() => {
  //   if (router.query['affiliate'] || router.query['affiliateCode']) {
  //     setAddon(false);
  //   }
  // }, [router.query]);

  // Provide context value
  const [uhx, setUhx] = useState(false);
  const [uhxCart, setUhxCart] = useState<AddonsType>(null);

  // Calculate uhx price
  const uhxPrice = useMemo(
    () => (uhxCart && !uhx ? UhxShopifyMap[store][uhxCart].price : 0),
    [uhxCart, store],
  );

  // Calculate total prices
  const dueEffectivePrice = useMemo(
    () => price + uhxPrice - effectiveDiscount.amount,
    [price, uhxPrice, effectiveDiscount.amount],
  );

  const effectivePrice = useMemo(
    () => price + uhxPrice - effectiveDiscount.amount,
    [price, uhxPrice, effectiveDiscount.amount],
  );

  // Checkout function
  const checkout = useCallback(async () => {
    setIsLoading(true);

    try {
      if (directCheckout) {
        const shopifyVariantId = productMap_home.variantId[store];
        if (shopifyVariantId) {
          const route = forwardShopifySearchParams(shopifyVariantId, 'x_home');
          await router.push(route);
        } else {
          console.error(
            'Error: Product variant ID not found for store:',
            store,
          );
        }
        return;
      }

      const shopifyVariantId = productMap_home.variantId[store];

      if (shopifyVariantId) {
        const lineItemsArray: Array<addToCartArgsType> = [];

        if (uhxCart) {
          lineItemsArray.push({
            shopifyVariantId: UhxShopifyMap[store as ShopifyStore][uhxCart]
              .variantId as string,
            productType: ProductType.GENERIC,
          });
        }

        lineItemsArray.push({
          shopifyVariantId: shopifyVariantId,
          productType: ProductType.GENERIC,
          params: {
            productType: 'HOME',
            noteAttributes: [],
          },
        });

        await CartClient.addToCart(tagBundle(lineItemsArray), true);
        return;
      }

      console.error('Error: Unable to initiate checkout process');
    } catch (error) {
      console.error('Error during checkout:', error);
    } finally {
      setIsLoading(false);
    }
  }, [
    directCheckout,
    forwardShopifySearchParams,
    router,
    store,
    uhxCart,
    CartClient,
  ]);

  const contextValue = useMemo<HomeContextType>(
    () => ({
      // Basic properties
      defaultCurrency: currency,
      currencySymbol,
      store,

      // Actions
      checkout,
      goToNextStep,
      isLoading,

      // Element references
      cartSummaryRef,
      coverageRef,
      addonRef,

      // Pricing details
      price,
      uhxPrice,
      effectivePrice,
      dueEffectivePrice,

      // Shipping and discounts
      shippingEstimate,
      affiliateDetails,
      campaignActive,
      affiliateDiscount,
      birthdayDiscount,
      effectiveDiscount: effectiveDiscount,
      referral,

      // UI helpers
      formatPrice,
      displayPrice,

      // UHX state
      uhx,
      setUhx,
      uhxCart,
      setUhxCart,
    }),
    [
      currency,
      currencySymbol,
      store,
      checkout,
      goToNextStep,
      isLoading,
      price,
      uhxPrice,
      effectivePrice,
      dueEffectivePrice,
      shippingEstimate,
      affiliateDetails,
      campaignActive,
      affiliateDiscount,
      birthdayDiscount,
      effectiveDiscount,
      coverageRef,
      referral,
      formatPrice,
      displayPrice,
      uhx,
      uhxCart,
    ],
  );

  return (
    <HomeContext.Provider value={contextValue}>{children}</HomeContext.Provider>
  );
};
